enum PropertyLocation {
  ENGLAND_WALES
  NORTHERN_IRELAND
  SCOTLAND
  ENGLAND
  WALES
}

enum PropertyWallType {
  STEEL
  COBB
  STANDARD_BRICK
  SINGLE_SKIN_BRICK
  WOOLAWAY
  CORNISH_TYPE_1
  CORNISH_TYPE_2
  OTHER_CONCRETE
  WIMPEY_NO_FINES
  LAING_EASIFORM
  TIMBER_FRAMED
  STONE
  OTHER
}

enum PropertyRoofType {
  FLAT_ROOF
  THATCHED_ROOF
  TILE_ROOF
  SLATE_ROOF
}

enum BuilderRegistrationScheme {
  NATIONAL_HOUSE_BUILDING_COUNCIL
  ZURICH_NEW_BUILD
  PREMIER_GUARANTEE_NEW_HOME_WARRANTY
  LOCAL_AUTHORITY_BUILDING_CONTROL_HOME_WARRANTY
  BUILDING_LIFE_PLANS_INSURANCE
  BUILD_ZONE_NEW_HOME_WARRANTY_SELF_BUILD
  CASTLE10_NEW_HOME_WARRANTY
  OTHER_BUILDER_REGISTRATION_SCHEME
}

enum PropertyType {
  HOUSE_BOAT
  HOUSE
  FLAT
  MAISONETTE
  BUNGALOW
  INVALID_PROPERTY_TYPE
}

enum PropertySubType {
  INVALID_SUBTYPE
  MID_TERRACED
  END_TERRACED
  DETACHED
  SEMI_DETACHED
  LINK_DETACHED
  TOWN_HOUSE
  PURPOSE_BUILT
  CONVERSION
}

enum PropertyWork {
  INVALID_WORK
  MAJOR_CONSTRUCTION
  STRUCTURAL_ALTERATIONS
  REFURBISHMENT
}

enum PropertyTenure {
  INVALID_PROPERTY_TENURE
  FREEHOLD
  FLYING_FREEHOLD
  SHARE_OF_FREEHOLD
  LEASEHOLD
  COMMONHOLD
  FEUDAL
  FEUHOLD
  CROSSOVER_LEASE
}

enum PropertyUse {
  PRIMARY_RESIDENCE
  SECOND_HOME
  HOLIDAY_HOME
  TENANTED
  LET_HOLIDAY_HOME
}

enum OccupantRelationship {
  TENANT
  CHILD
  PARTNER_SPOUSE
  OTHER_FAMILY
  OTHER
}

enum TenancyType {
  AST
  ASSURED_TENANCY
  NON_ASSURED_TENANCY
  EXCLUDED_TENANCY
  REGULATED_TENANCY
  COMPANY_LET
}

enum TenantType {
  DSS
  STUDENT
  PROFESSIONAL
  COMMERCIAL
  FAMILY
}

enum BuilderRegistrationSchemeType {
  INVALID_BUILDER_REGISTRATION_SCHEME
  NATIONAL_HOUSE_BUILDING_COUNCIL
  ZURICH_NEW_BUILD
  PREMIER_GUARANTEE_NEW_HOME_WARRANTY
  LOCAL_AUTHORITY_BUILDING_CONTROL_HOME_WARRANTY
  BUILDING_LIFE_PLANS_INSURANCE
  BUILD_ZONE_NEW_HOME_WARRANTY_SELF_BUILD
  CASTLE10_NEW_HOME_WARRANTY
  OTHER_BUILDER_REGISTRATION_SCHEME
}

enum SharedOwnershipType {
  PRIVATE_SHARED_EQUITY
  HELP_TO_BUY
  RIGHT_TO_BUY
  SHARE_DOWNERSHIP
  FAMILY_ASSIST_SCHEME
  NEW_BUY_SCHEME
  MORTGAGE_GUARANTEE_SCHEME
  FIRST_HOMES_SCHEME
  INVALID_SHARED_OWNERSHIP_TYPE
}

enum PropertyParking {
  NONE
  DRIVEWAY
  SINGLE_GARAGE
  DOUBLE_GARAGE
  PARKING_SPACE
}

type Builder {
  name: String
  registration_scheme: BuilderRegistrationScheme
}

type Occupant {
  name: String
  is_minor: Boolean
  date_of_birth: String
  relationship: OccupantRelationship
  tenancy_type: TenancyType
  tenant_type: TenantType
}

type PropertyVersion implements VersionedObject {
  id: ID!
  property_id: String!
  created_at: String
  version: Int
  version_created_at: String
  details: Property!
  versions: [VersionWithChangedFields]
}

type ExternalPropertySource {
  id: String
  name: ExternalIdNames
}

type ExternalProperty {
  id: String
  source: ExternalPropertySource
}

type Property {
  id: ID!
  property_id: String!
  acre_property_id: String
  current_energy_rating: String
  occupants: [Occupant]
  property_location: PropertyLocation
  property_valuation: String
  property_price: String
  specified_rebuild_cost: Int
  property_type: PropertyType
  registered_owners: [ClientVersion]
  registered_owners_details: [String!]
  target_registered_owners_details: [ID!]
  location: PropertyLocation
  year_built: Int
  price: String
  valuation: String
  valuation_date: String
  right_to_buy_valuation: String
  is_private_sale: Boolean
  purchasing_as_sitting_tennant: Boolean
  builder: Builder
  address: LutherAddress
  type: PropertyType
  sub_type: PropertySubType
  tenure: PropertyTenure
  property_tenure: PropertyTenure
  use: PropertyUse
  flying_freehold_percentage: Int
  leasehold_service_charge: String
  attributes: PropertyAttributes
  mortgages: [Mortgage]
  mortgagesCp: [Mortgage!]
  clear_floor_between_retail: Boolean
  is_new_build: Boolean
  property_builder_name: String
  new_build_discounts_or_incentives_value: String
  builder_registration_scheme: BuilderRegistrationSchemeType
  year_property_built: Int
  monthly_management_expenses: String
  wall_type: PropertyWallType
  roof_type: PropertyRoofType
  intend_to_let_or_run_business: Boolean
  intend_to_let_or_run_business_description: String
  property_use: PropertyUse
  number_of_bedrooms: Int
  number_of_bathrooms: Int
  is_property_ex_local_authority: Boolean
  is_property_furnished: Boolean
  monthly_rental_income: String
  shared_ownership_monthly_rent: String
  is_shared_ownership: Boolean
  shared_ownership_type: SharedOwnershipType
  shared_ownership_share: Int
  help_to_buy_loan_amount: String
  shared_equity_monthly_interest: String
  shared_ownership_scheme_provider: String
  shared_equity_repayment_details: String
  is_property_habitable: Boolean
  is_work_planned_on_property: Boolean
  work_planned_on_property: PropertyWork
  gross_development_value: String
  cost_of_works: String
  is_unencumbered: Boolean
  btl_has_lived: Boolean
  btl_will_lived: Boolean
  btl_is_operating_a_business: Boolean
  is_house_of_multiple_occupancy: Boolean
  number_of_lettable_rooms: Int
  is_multi_unit_freehold_block: Boolean
  case_status: CaseStatus
  purchase_date: String
  transition_mortgage_versions: [TransitionMortgageVersion!]
  external: [ExternalProperty]
}

type PropertyAttributes {
  num_flats_in_block: Int
  num_floors_in_block: Int
  floor_of_flat: Int
  does_the_property_have_a_lift: Boolean
  does_the_property_have_deck_access: Boolean
  is_property_over_retail: Boolean
  property_converted_in_last_ten_years: Boolean
  percent_of_property_used_for_residential: Int
  remaining_percent_property_use_description: String
  freehold_percent_floor_area: Int
  is_single_dwelling: Boolean
  is_designated_defective: Boolean
  is_near_environmental_hazards: Boolean
  years_left_on_lease: Int
  leasehold_ground_rent: String
  leasehold_service_charge: String
  solar_panels_on_property: Boolean
  solar_panels_equipment_in_use: Boolean
  roof_subject_to_lease: Boolean
  is_property_currently_rented: Boolean
  number_of_kitchens: Int
  number_of_reception_rooms: Int
  has_annex: Boolean
  property_parking: PropertyParking
}

input ExternalPropertySourceInput {
  id: String
  name: ExternalIdNames
}

input ExternalPropertyInput {
  id: String
  source: ExternalPropertySourceInput
}

input PropertyInput {
  occupants: [OccupantInput]
  registered_owners_details: [ID!]
  target_registered_owners_details: [ID!]
  property_location: PropertyLocation
  property_price: String
  property_valuation: String
  specified_rebuild_cost: Int
  property_valuation_date: String
  right_to_buy_valuation: String
  is_private_sale: Boolean
  purchasing_as_sitting_tennant: Boolean
  address: LutherAddressInput
  property_type: PropertyType
  sub_type: PropertySubType
  property_tenure: PropertyTenure
  property_use: PropertyUse
  flying_freehold_percentage: Int
  leasehold_service_charge: String
  leasehold_ground_rent: String
  num_floors_in_block: Int
  num_flats_in_block: Int
  floor_of_flat: Int
  years_left_on_lease: Int
  is_property_over_retail: Boolean
  does_the_property_have_a_lift: Boolean
  property_converted_in_last_ten_years: Boolean
  clear_floor_between_retail: Boolean
  is_new_build: Boolean
  property_builder_name: String
  new_build_discounts_or_incentives_value: String
  builder_registration_scheme: BuilderRegistrationSchemeType
  year_property_built: Int
  monthly_management_expenses: String
  wall_type: PropertyWallType
  roof_type: PropertyRoofType
  intend_to_let_or_run_business: Boolean
  intend_to_let_or_run_business_description: String
  number_of_bedrooms: Int
  number_of_bathrooms: Int
  is_property_ex_local_authority: Boolean
  is_property_furnished: Boolean
  monthly_rental_income: String
  shared_ownership_monthly_rent: String
  is_shared_ownership: Boolean
  shared_ownership_type: SharedOwnershipType
  shared_ownership_share: Int
  help_to_buy_loan_amount: String
  shared_equity_monthly_interest: String
  shared_ownership_scheme_provider: String
  shared_equity_repayment_details: String
  is_property_habitable: Boolean
  is_work_planned_on_property: Boolean
  work_planned_on_property: PropertyWork
  gross_development_value: String
  cost_of_works: String
  is_unencumbered: Boolean
  acre_property_id: String
  current_energy_rating: String
  btl_has_lived: Boolean
  btl_will_lived: Boolean
  btl_is_operating_a_business: Boolean
  is_house_of_multiple_occupancy: Boolean
  number_of_lettable_rooms: Int
  is_multi_unit_freehold_block: Boolean
  number_of_kitchens: Int
  number_of_reception_rooms: Int
  has_annex: Boolean
  property_parking: PropertyParking
  solar_panels_on_property: Boolean
  does_the_property_have_deck_access: Boolean
  purchase_date: String
  external: [ExternalPropertyInput]
}

type PropertyEntityVersion {
  property_id: String!
  version: Int!
}

input OccupantInput {
  name: String
  is_minor: Boolean
  date_of_birth: String
  relationship: OccupantRelationship
  tenancy_type: TenancyType
  tenant_type: TenantType
}

type CaseSummary {
  calculated_values: SummaryCalculatedValues
  case: CaseEntity
  properties: [PropertyVersion!]
  mortgages: [MortgageWithProduct!]
  clients: [ClientVersion!]
}

type CaseEntity {
  case_id: String
}

type SummaryCalculatedValues {
  mortgage_debt: String
  mortgage_monthly_payments: String
  remaining_mortgage_term_months: Int
}

input CaseSummaryInput {
  calculated_values: SummaryCalculatedValuesInput
}

input SummaryCalculatedValuesInput {
  mortgage_debt: String
  mortgage_monthly_payments: String
  remaining_mortgage_term_months: Int
}
